/**
 * Ranger Favored Enemies
 * SRD 5.1 Content
 *
 * Rangers choose favored enemy types at levels 1, 6, and 14.
 * Each selection grants advantage on Wisdom (Survival) checks to track
 * and Intelligence checks to recall information about these creatures.
 */

export interface FavoredEnemy {
  id: string;
  name: string;
  description: string;
  examples?: string; // Example creatures of this type
}

/**
 * All Favored Enemy types from SRD 5.1
 */
export const FAVORED_ENEMIES: FavoredEnemy[] = [
  {
    id: 'aberrations',
    name: 'Aberrations',
    description:
      'Utterly alien beings from beyond the planes. Many have innate magical abilities and warped anatomy.',
    examples: 'Aboleths, beholders, mind flayers',
  },
  {
    id: 'beasts',
    name: 'Beasts',
    description:
      'Nonhumanoid creatures that are a natural part of the fantasy ecology. Many are animals familiar from the real world.',
    examples: 'Bears, wolves, giant spiders',
  },
  {
    id: 'celestials',
    name: 'Celestials',
    description:
      'Creatures native to the Upper Planes. Many are servants of deities, employed as messengers or agents.',
    examples: 'Angels, couatls, pegasi',
  },
  {
    id: 'constructs',
    name: 'Constructs',
    description:
      'Made, not born. Animated through magic, given physical form and purpose by their creators.',
    examples: 'Golems, animated armor, shield guardians',
  },
  {
    id: 'dragons',
    name: 'Dragons',
    description:
      'Large reptilian creatures of ancient origin and tremendous power. True dragons include chromatic and metallic varieties.',
    examples: 'Chromatic dragons, metallic dragons, dragon turtles',
  },
  {
    id: 'elementals',
    name: 'Elementals',
    description:
      'Creatures native to the elemental planes. Some are sentient, others are mindless forces of primal energy.',
    examples: 'Fire elementals, water elementals, gargoyles',
  },
  {
    id: 'fey',
    name: 'Fey',
    description:
      'Magical creatures closely tied to the forces of nature. They dwell in twilight groves and misty forests.',
    examples: 'Dryads, pixies, satyrs',
  },
  {
    id: 'fiends',
    name: 'Fiends',
    description:
      'Wicked creatures native to the Lower Planes. Demons and devils are the most common fiends.',
    examples: 'Demons, devils, imps',
  },
  {
    id: 'giants',
    name: 'Giants',
    description:
      'Humanlike creatures of great stature and power. The six varieties include cloud, fire, frost, hill, stone, and storm giants.',
    examples: 'Hill giants, frost giants, ogres, trolls',
  },
  {
    id: 'monstrosities',
    name: 'Monstrosities',
    description:
      'Frightening creatures not ordinary or natural. Some are results of magical experimentation, others are products of terrible curses.',
    examples: 'Griffons, manticores, owlbears',
  },
  {
    id: 'oozes',
    name: 'Oozes',
    description:
      'Gelatinous creatures that rarely have a fixed shape. Mostly subterranean, they prey on anything that comes within reach.',
    examples: 'Black puddings, gelatinous cubes, gray oozes',
  },
  {
    id: 'plants',
    name: 'Plants',
    description:
      'Vegetable creatures that have some degree of mobility and usually a predatory nature.',
    examples: 'Shambling mounds, treants, blights',
  },
  {
    id: 'undead',
    name: 'Undead',
    description:
      'Once-living creatures brought to a horrifying state through necromancy or a curse. They hate the living.',
    examples: 'Zombies, vampires, liches, ghosts',
  },
  {
    id: 'humanoids',
    name: 'Humanoids (Two Races)',
    description:
      'Creatures with human-like form. Choosing humanoids grants two specific humanoid races as favored enemies.',
    examples: 'Orcs, goblins, gnolls, hobgoblins',
  },
];

/**
 * Humanoid race options when humanoids is chosen
 */
export const HUMANOID_RACES: string[] = [
  'Dwarves',
  'Elves',
  'Gnolls',
  'Gnomes',
  'Goblinoids',
  'Halflings',
  'Humans',
  'Kobolds',
  'Lizardfolk',
  'Orcs',
  'Sahuagin',
];

/**
 * Levels at which Rangers choose favored enemies
 */
export const FAVORED_ENEMY_LEVELS = [1, 6, 14];

/**
 * Get a favored enemy by ID
 */
export function getFavoredEnemy(id: string): FavoredEnemy | undefined {
  return FAVORED_ENEMIES.find((e) => e.id === id);
}

/**
 * Get all favored enemy IDs
 */
export function getFavoredEnemyIds(): string[] {
  return FAVORED_ENEMIES.map((e) => e.id);
}

/**
 * Get the number of favored enemies a Ranger can have at a given level
 */
export function getFavoredEnemyCount(rangerLevel: number): number {
  return FAVORED_ENEMY_LEVELS.filter((l) => rangerLevel >= l).length;
}
